<template>
    <div class="chat-container">
      <div class="chat-header">
          <h2>404 SupportBot</h2>
          <v-spacer></v-spacer>
          <div class="mx-3">
            <button v-if="hasActiveConversation" @click="handleReset" class="reset-btn">New Chat</button>
          </div>
          <div>
            <button @click="handleLogout" class="reset-btn">Logout</button>
          </div>
      </div>
      <ChatWindow/>
      <MessageInput :disabled="!isProductSelected" @send-message="handleSendMessage"/>
      <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
      </div>
    </div>
</template>

<script>
import { mapGetters,mapActions } from 'vuex';
import ChatWindow from './ChatWindow.vue';
import MessageInput from './MessageInput.vue';
import { authService } from '@/services/AuthService';

export default {
    name:'ChatContainer',
    components:{
        ChatWindow,
        MessageInput
    },
    props:{
        conversationId:{
            type:String,
            default:null
        }
    },
    computed:{
        ...mapGetters(['errorMessage','isProductSelected','hasActiveConversation','isInitialized','isAuthenticated','authError'])
    },
    methods:{
        ...mapActions(['initializeBot','sendMessage','resetConversation','authenticateUser']),
        handleSendMessage(payload){
            this.sendMessage(payload);
        },
        async handleReset(){
            try {
                await this.resetConversation();
                if (this.$route.path !== '/') {
                    await this.$router.push('/').catch(() => {});
                }
            } catch (error) {
                console.error('Error resetting conversation:', error);
            }
        },
        async initializeApp() {
          const authSuccess = await this.authenticateUser();

          if(authSuccess && !this.isInitialized){
            await this.initializeBot();
          } else if (!authSuccess && this.$route.path !== '/login') {
            await this.$router.replace('/login');
          }
        },
        async handleLogout() {
          const endSessionUrl = await authService.logout(
            window.location.origin + '/login',
            false
          );

          this.$store.commit('SET_AUTHENTICATED', false);
          this.$store.commit('SET_USER_EMAIL', '');
          this.$store.commit('SET_AUTH_ERROR', null);

          await this.resetConversation();
          window.location.assign(endSessionUrl);
        }
    },
    async mounted(){
      const routeConversationId = this.$route.params.conversationId;
      const stateConversationId = this.$store.state.conversationId;
      
      if (routeConversationId && !stateConversationId) {
        // Conversation state lost on refresh - redirect to home
        await this.$router.replace('/').catch(() => {});
      }
      await this.initializeApp();
    },
    watch: {
    '$route'(to) {
        if (to.path === '/' && !this.hasActiveConversation && !this.isInitialized && this.isAuthenticated) {
            this.initializeBot();
        }
        }
    }
}
</script>

<style scoped>
.chat-container {
  width: 100%;
  max-width: none;
  height: 100vh;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* Centered container on larger screens */
@media (min-width: 600px) {
  .chat-container {
    max-width: 50%;
    height: calc(100vh - 80px);
    margin: 20px auto;
    border-radius: 12px;
  }
}

.chat-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.reset-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.reset-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4444;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(255, 68, 68, 0.3);
  z-index: 1001;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
.auth-error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  border-radius: 12px;
}

.auth-error-modal {
  background: white;
  padding: 40px;
  border-radius: 12px;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.auth-error-modal h3 {
  margin: 0 0 16px 0;
  color: #ff4444;
  font-size: 24px;
}

.auth-error-modal p {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  line-height: 1.5;
}

.error-hint {
  color: #666;
  font-size: 14px;
  font-style: italic;
}

</style>